import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatButton } from '@angular/material/button';
import { catchError, interval, of, retry, Subject, switchMap, takeUntil } from 'rxjs';
import { EventTypeEnum, Notification } from 'app/layout/common/notifications/notifications.types';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { NotificationService } from 'app/shared/services/notification.service';
import { DatePipe } from '@angular/common';
import { CellRenderService } from 'app/shared/services/cell-render.service';
import moment from 'moment';

@Component({
    selector       : 'notifications',
    templateUrl    : './notifications.component.html',
    styleUrls    : ['./notifications.component.scss'],
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'notifications'
})
export class NotificationsComponent implements OnInit, OnDestroy, AfterViewInit 
{
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;
    @ViewChild('defaultTemplate', { static: false }) defaultTemplate: TemplateRef<any>;
    @ViewChild('pobTemplate', { static: false }) pobTemplate: TemplateRef<any>;
    @ViewChild('pobCancelledTemplate', { static: false }) pobCancelledTemplate: TemplateRef<any>;
    @ViewChild('autoChangeTemplate', { static: false }) autoChangeTemplate: TemplateRef<any>;

    notifications: Notification[];
    unreadCount: number = 0;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    searchTerm: string;
    filteredResults: Notification[] = [];

    

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _notificationService: NotificationService,
        private datePipe: DatePipe,
        private _cellRendererService: CellRenderService
    )
    {
    }
    ngAfterViewInit() {
        
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        this._changeDetectorRef.detectChanges();

        interval(60000)
        .pipe(
        takeUntil(this._unsubscribeAll),
        switchMap(() => 
        this._notificationsService.getAll()
        .pipe(
            retry(3),
            catchError((error) => {
                return of([]);
            })
        ))
        )
        .subscribe((notifications: any) => {
        // Handle the result here if needed
        });

        // Subscribe to notification changes
        this._notificationsService.notifications$
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((notifications: Notification[]) => {
            // Map the content string to objectContent
            this.notifications = notifications.map(notification => ({
                ...notification,
                objectContent: JSON.parse(notification.content)
            }));
            // Also map it for filteredResults if needed
            this.filteredResults = this.notifications;

            // Calculate the unread count
            this._calculateUnreadCount();

            // Mark for check
            this._changeDetectorRef.markForCheck();
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if ( this._overlayRef )
        {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    applyFilter(): void {
        // Implement your filtering logic here
        // For example, you might filter an array of items
        // Replace this with your actual filtering logic based on your data structure
        this.filteredResults = this.notifications.filter(item =>
          item.title.toLowerCase().includes(this.searchTerm.toLowerCase())
          || item.reason?.toLowerCase()?.includes(this.searchTerm.toLowerCase())
          || item.event.toLowerCase().includes(this.searchTerm.toLowerCase())
          || item.terminal.toLowerCase().includes(this.searchTerm.toLowerCase())
        );
      }

    getBgClass(eventType: string): string {
        if (Object.values(EventTypeEnum).includes(eventType as EventTypeEnum)) {
          switch (eventType as EventTypeEnum) {
            case EventTypeEnum.POB_DEPARTURE:
            case EventTypeEnum.POB_BERTHING:
              return "";
            case EventTypeEnum.POB_CANCELLED:
                return "";
            default:
              return ""; 
          }
        } else {
          return "bg-gray-200 text-blue-500 dark:bg-gray-600 dark:text-blue-50";
        }
      }

    isPobEvent(event: string): boolean {
        return event === EventTypeEnum.POB_BERTHING || event === EventTypeEnum.POB_DEPARTURE;
    }
    
    isPobCancelledEvent(event: string): boolean {
        return event === EventTypeEnum.POB_CANCELLED;
    }
    

    getTemplateForNotification(notification: any): TemplateRef<any> {
        if (this.isPobCancelledEvent(notification.event)) {
          return this.pobCancelledTemplate;
        } else if (this.isPobEvent(notification.event)) {
          return this.pobTemplate;
        } else if (notification.notificationType === 2) {
          return this.autoChangeTemplate;
        } else {
          return this.defaultTemplate;
        }
      }
      

    formatDateTime(value: any, dataType: number): string {

        if (!value)
            return 'TBD';
        
        if (dataType == 0) {
            const utcMoment = moment.utc(value);

            // Format the UTC date with the specified format
            const formattedUtcDate = utcMoment.format('ddd DD/MMM HH:mm');
        
            return formattedUtcDate;
        }
        else
             return this._cellRendererService.renderHour({value: value});
    }

    /**
     * Open the notifications panel
     */
    openPanel(): void
    {
        // Return if the notifications panel or its origin is not defined
        if ( !this._notificationsPanel || !this._notificationsOrigin )
        {
            return;
        }

        // Create the overlay if it doesn't exist
        if ( !this._overlayRef )
        {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void
    {
        this._overlayRef.detach();
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void
    {
        // Mark all as read
        this._notificationsService.markAllAsRead(this.notifications.filter(n => !n.read).map(n => n.id)).subscribe();
    }

    /**
     * Toggle read status of the given notification
     */
    toggleRead(notification: Notification): void
    {
        // Toggle the read status
        notification.read = !notification.read;

        // Update the notification
        this._notificationsService.update([notification.id], notification).subscribe();
    }


    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void
    {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop     : true,
            backdropClass   : 'fuse-backdrop-on-mobile',
            scrollStrategy  : this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                                  .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                                  .withLockedPosition(true)
                                  .withPush(true)
                                  .withPositions([
                                      {
                                          originX : 'start',
                                          originY : 'bottom',
                                          overlayX: 'start',
                                          overlayY: 'top'
                                      },
                                      {
                                          originX : 'start',
                                          originY : 'top',
                                          overlayX: 'start',
                                          overlayY: 'bottom'
                                      },
                                      {
                                          originX : 'end',
                                          originY : 'bottom',
                                          overlayX: 'end',
                                          overlayY: 'top'
                                      },
                                      {
                                          originX : 'end',
                                          originY : 'top',
                                          overlayX: 'end',
                                          overlayY: 'bottom'
                                      }
                                  ])
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    /**
     * Calculate the unread count
     *
     * @private
     */
    private _calculateUnreadCount(): void
    {
        let count = 0;

        if ( this.notifications && this.notifications.length )
        {
            count = this.notifications.filter(notification => !notification.read).length;
        }

        this.unreadCount = count;
    }
}
